/**
 * MapLibre helpers for the hearth drawing surface.
 *
 * Two GeoJSON sources, one for snap-to-road segments (yellow/orange per
 * S-04-04 acceptance) and one for free-draw segments (pink per S-04-05).
 * Waypoints are rendered as small dots on a third source so the user
 * sees exactly where they clicked.
 *
 * We intentionally render snap and free segments on **separate** layers
 * with distinct colours so the drawing surface communicates which mode
 * each segment came from. Once the hearth is saved (S-04-06) the colour
 * collapses to the priority-coded style — that styling lives elsewhere.
 *
 * No external network calls happen here. Sources are GeoJSON in-memory.
 */

import type {
  GeoJSONSource,
  Map as MapLibreMap,
  LineLayerSpecification,
  CircleLayerSpecification,
} from 'maplibre-gl';
import type { DrawingState } from './drawingState';
import type { Coordinate } from './types';

// Minimal local GeoJSON typings. The `@types/geojson` package is on the
// transitive node_modules tree but is not in `tsconfig.app.json`'s
// `types` array — and we don't want to widen that array for one helper.
// MapLibre source `setData` accepts any RFC-7946-shaped object, so a
// narrow inline definition is enough.
type Position = [number, number];
interface LineStringGeometry {
  readonly type: 'LineString';
  readonly coordinates: ReadonlyArray<Position>;
}
interface PointGeometry {
  readonly type: 'Point';
  readonly coordinates: Position;
}
interface Feature<G> {
  readonly type: 'Feature';
  readonly geometry: G;
  readonly properties: Record<string, unknown>;
}
interface FeatureCollection<G> {
  readonly type: 'FeatureCollection';
  readonly features: ReadonlyArray<Feature<G>>;
}

export const DRAW_SOURCE_IDS = {
  snapSegments: 'lyktan-draw-snap-segments',
  freeSegments: 'lyktan-draw-free-segments',
  waypoints: 'lyktan-draw-waypoints',
} as const;

export const DRAW_LAYER_IDS = {
  snapSegments: 'lyktan-draw-snap-segments-line',
  freeSegments: 'lyktan-draw-free-segments-line',
  waypoints: 'lyktan-draw-waypoints-circle',
} as const;

const COLOR_SNAP = '#ffb84d'; // gul/orange — snap-to-road i UI-kontrakt vy 7
const COLOR_FREE = '#ff6fa3'; // rosa — fri ritning i UI-kontrakt vy 7
const COLOR_WAYPOINT = '#ffe6b8';

function emptyLineCollection(): FeatureCollection<LineStringGeometry> {
  return { type: 'FeatureCollection', features: [] };
}

function emptyPointCollection(): FeatureCollection<PointGeometry> {
  return { type: 'FeatureCollection', features: [] };
}

/**
 * Idempotent: register sources/layers once. Calling twice is a no-op.
 * We keep separate sources for snap vs free so each layer can have a
 * distinct colour without per-feature `paint`-expressions.
 */
// Local cast helper: turn our narrow FeatureCollection types into the
// shape MapLibre expects for `addSource.data` and `setData`. We do this
// in one place so the structural intent stays obvious in the call sites.
// Using `unknown` here is deliberate — `@types/geojson`'s `Geometry`
// union is much wider than what we actually emit (we only ever use
// LineString and Point), but bringing those generics through every call
// site bloats the surface for no runtime benefit.
type MapLibreGeoJSONData = Parameters<GeoJSONSource['setData']>[0];
function asMapLibreData(value: object): MapLibreGeoJSONData {
  return value as unknown as MapLibreGeoJSONData;
}

export function ensureDrawingLayers(map: MapLibreMap): void {
  if (!map.getSource(DRAW_SOURCE_IDS.snapSegments)) {
    map.addSource(DRAW_SOURCE_IDS.snapSegments, {
      type: 'geojson',
      data: asMapLibreData(emptyLineCollection()),
    });
  }
  if (!map.getSource(DRAW_SOURCE_IDS.freeSegments)) {
    map.addSource(DRAW_SOURCE_IDS.freeSegments, {
      type: 'geojson',
      data: asMapLibreData(emptyLineCollection()),
    });
  }
  if (!map.getSource(DRAW_SOURCE_IDS.waypoints)) {
    map.addSource(DRAW_SOURCE_IDS.waypoints, {
      type: 'geojson',
      data: asMapLibreData(emptyPointCollection()),
    });
  }

  if (!map.getLayer(DRAW_LAYER_IDS.snapSegments)) {
    const layer: LineLayerSpecification = {
      id: DRAW_LAYER_IDS.snapSegments,
      type: 'line',
      source: DRAW_SOURCE_IDS.snapSegments,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': COLOR_SNAP,
        'line-width': 4,
        'line-opacity': 0.95,
      },
    };
    map.addLayer(layer);
  }
  if (!map.getLayer(DRAW_LAYER_IDS.freeSegments)) {
    const layer: LineLayerSpecification = {
      id: DRAW_LAYER_IDS.freeSegments,
      type: 'line',
      source: DRAW_SOURCE_IDS.freeSegments,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': COLOR_FREE,
        'line-width': 4,
        'line-dasharray': [1.5, 1.5],
        'line-opacity': 0.95,
      },
    };
    map.addLayer(layer);
  }
  if (!map.getLayer(DRAW_LAYER_IDS.waypoints)) {
    const layer: CircleLayerSpecification = {
      id: DRAW_LAYER_IDS.waypoints,
      type: 'circle',
      source: DRAW_SOURCE_IDS.waypoints,
      paint: {
        'circle-radius': 5,
        'circle-color': COLOR_WAYPOINT,
        'circle-stroke-color': '#0e1014',
        'circle-stroke-width': 1.5,
      },
    };
    map.addLayer(layer);
  }
}

/** Tear down on unmount or before a style swap. Idempotent. */
export function removeDrawingLayers(map: MapLibreMap): void {
  for (const id of Object.values(DRAW_LAYER_IDS)) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  for (const id of Object.values(DRAW_SOURCE_IDS)) {
    if (map.getSource(id)) map.removeSource(id);
  }
}

function toLngLat(c: Coordinate): [number, number] {
  return [c.longitude, c.latitude];
}

/** Build the three GeoJSON FeatureCollections for the given drawing state. */
export function buildDrawingFeatureCollections(state: DrawingState): {
  snap: FeatureCollection<LineStringGeometry>;
  free: FeatureCollection<LineStringGeometry>;
  waypoints: FeatureCollection<PointGeometry>;
} {
  const snapFeatures: Feature<LineStringGeometry>[] = [];
  const freeFeatures: Feature<LineStringGeometry>[] = [];

  for (const segment of state.segments) {
    if (segment.points.length < 2) continue;
    const feature: Feature<LineStringGeometry> = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: segment.points.map(toLngLat),
      },
      properties: { mode: segment.mode },
    };
    if (segment.mode === 'snap-to-road') snapFeatures.push(feature);
    else freeFeatures.push(feature);
  }

  const waypointFeatures: Feature<PointGeometry>[] = state.waypoints.map((w, idx) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: toLngLat(w) },
    properties: { index: idx },
  }));

  return {
    snap: { type: 'FeatureCollection', features: snapFeatures },
    free: { type: 'FeatureCollection', features: freeFeatures },
    waypoints: { type: 'FeatureCollection', features: waypointFeatures },
  };
}

/** Push the latest drawing state into the registered sources. */
export function syncDrawingFeatures(map: MapLibreMap, state: DrawingState): void {
  const collections = buildDrawingFeatureCollections(state);
  const snap = map.getSource(DRAW_SOURCE_IDS.snapSegments) as GeoJSONSource | undefined;
  // MapLibre's setData accepts any RFC-7946-shaped object. Our local
  // `FeatureCollection<...>` type is structurally compatible; the cast
  // tells the compiler so without bringing in the global geojson types.
  snap?.setData(asMapLibreData(collections.snap));
  const free = map.getSource(DRAW_SOURCE_IDS.freeSegments) as GeoJSONSource | undefined;
  free?.setData(asMapLibreData(collections.free));
  const waypoints = map.getSource(DRAW_SOURCE_IDS.waypoints) as GeoJSONSource | undefined;
  waypoints?.setData(asMapLibreData(collections.waypoints));
}
